import React from "react";
import { TrendingUp, Scale, DollarSign, Clock } from "lucide-react";
import { DashboardMetrics } from "@/lib/api";

interface MetricsGridProps {
  metrics: DashboardMetrics | undefined;
  isLoading: boolean;
}

export default function MetricsGrid({ metrics, isLoading }: MetricsGridProps) {
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="metric-card rounded-xl p-6 shadow-sm animate-pulse">
            <div className="h-4 bg-gray-200 rounded mb-4"></div>
            <div className="h-8 bg-gray-200 rounded mb-2"></div>
            <div className="h-3 bg-gray-200 rounded w-2/3"></div>
          </div>
        ))}
      </div>
    );
  }

  if (!metrics) return null;

  const metricCards = [
    {
      title: "Visits Today",
      value: metrics.dailyVisits,
      icon: TrendingUp,
      color: "text-primary",
      bgColor: "bg-primary/10",
      change: "+12%",
      changeColor: "text-green-600 bg-green-50"
    },
    {
      title: "Active Sessions",
      value: metrics.activeSessions,
      icon: Scale,
      color: "text-accent",
      bgColor: "bg-accent/10",
      change: `${metrics.activeSessions} Active`,
      changeColor: "text-orange-600 bg-orange-50"
    },
    {
      title: "Total Weight Today",
      value: `${metrics.totalWeight.toFixed(1)}T`,
      icon: DollarSign,
      color: "text-green-600",
      bgColor: "bg-green-100",
      change: "R 45,230",
      changeColor: "text-blue-600 bg-blue-50"
    },
    {
      title: "Avg Cycle Time",
      value: `${metrics.averageTime.toFixed(1)}min`,
      icon: Clock,
      color: "text-red-600",
      bgColor: "bg-red-100",
      change: "2 Alerts",
      changeColor: "text-red-600 bg-red-50"
    }
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      {metricCards.map((metric, index) => (
        <div key={index} className="metric-card rounded-xl p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className={`w-12 h-12 ${metric.bgColor} rounded-lg flex items-center justify-center`}>
              <metric.icon className={`w-6 h-6 ${metric.color}`} />
            </div>
            <span className={`text-xs font-medium px-2 py-1 rounded ${metric.changeColor}`}>
              {metric.change}
            </span>
          </div>
          <div className="text-2xl font-bold text-foreground mb-1" data-testid={`metric-${metric.title.toLowerCase().replace(/\s+/g, '-')}`}>
            {metric.value}
          </div>
          <div className="text-sm text-muted-foreground">{metric.title}</div>
        </div>
      ))}
    </div>
  );
}
